import _ from 'lodash';
import React, { Suspense, useContext, useEffect, useState } from 'react';
import VideoConference from '../../../globalAlt/components/videoConference/VideoConference';
import { appNotificationTypes } from '../../appNotifications/AppNotificationsManager';
import { ParentContext } from '../../cnr/contexts/ParentContext';
import { appFormTypes } from '../../enums/appFormTypes';
import { gEnums } from '../../enums/globalEnums';
import { createRefPath_client } from '../../firestoreData/appData/appRefPaths';
import Wrapper, { wrapperTypes } from '../../wrappers/Wrapper';
import NoData from '../alerts/NoData';
import SuspenseDimmer from '../alerts/SuspenseDimmer';
import UiSaveButtons from '../buttons/UiSaveButtons';

const AppForm = React.lazy(() => import('../forms/appForms/AppForm'));
const AppMeetingsManagement = React.lazy(() => import('../../appMeetings/AppMeetingsManagement'));
const AppNotificationsManager = React.lazy(() => import('../../appNotifications/AppNotificationsManager'));
const AppUserPages = React.lazy(() => import('./AppUserPages'));
const AppUserTicketingWithProvider = React.lazy(() => import('../../../projectSpecific/ticketing/components/AppUserTicketing'));
const ImageGallery = React.lazy(() => import('../imaging/ImageGallery'));
const ProfileImage = React.lazy(() => import('../imaging/ProfileImage'));
const QrCode = React.lazy(() => import('../../pageItem/modification/pageItemActions/QrCode'));
const UserConnections = React.lazy(() => import('./UserConnections'));
const UserConversations = React.lazy(() => import('./UserConversations'));
const UserEvents = React.lazy(() => import('./UserEvents'));
const UserFamily = React.lazy(() => import('./UserFamily'));
const UserFavorites = React.lazy(() => import('./UserFavorites'));
const UserNotes = React.lazy(() => import('./UserNotes'));

const AppUserProfileItem = (props) => {

  const { pageType, opts } = props
  const { qrCodeProps } = opts ?? {}

  // parentContext
  const parentContext = useContext(ParentContext);
  const { states, handlers, fns } = parentContext ?? {}
  const { appUser_state, eventInfo_state, paps_state, currentSettings_state } = states
  const { appUser_handlers } = handlers
  const { database_fns, appUser_fns } = fns

  const { pathViews, view, viewKey } = paps_state ?? {}
  const { aps_appUserSettings } = _.get(currentSettings_state, 'gvviSettings', {});
  const { appUser, appUsers, updating, confirmation } = appUser_state ?? {}
  const { email } = appUser ?? {}

  const { staticViews } = eventInfo_state ?? {}

  const {
    appUserCollection,
  } = aps_appUserSettings ?? {}

  const appUserAccess = useAppUserAccess()
  const profileData = appUser_fns.get_profileData()
  const { profileOriginType } = profileData ?? {}

  const aaua = appUser_fns.validateAccess_appUser({ pathViews, appUserCollection, view, viewKey, appUserAccess })

  const [formData, setFormData] = useState({})

  const getAppUserProfileInfo = () => {
    if (pathViews && pathViews[appUserCollection]) {
      const staticView = staticViews && staticViews[appUserCollection]
      const appUserKey = pathViews[appUserCollection]
      const staticViewItem = staticView && appUserKey ? staticView[appUserKey] : {}
      const { email } = staticViewItem ?? {}
      if (email) {
        const cb = (results) => { setFormData(results) }
        const refPath = createRefPath_client(pathViews, ['profiles', email])
        database_fns.get_doc({ refPath, callback: cb })
      }
    }
  }

  useEffect(() => {
    if (profileData) {
      switch (pageType) {
        case gEnums.appUserProfilePageTypes.appUserAboutMe:
        case gEnums.appUserProfilePageTypes.appUserBio:
        case gEnums.appUserProfilePageTypes.appUserProfile:
          getAppUserProfileInfo()
          break;
        default:
          setFormData(profileData[pageType])
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [pageType]);

  const handleSave = () => {
    let _profileData = { ...profileData }
    switch (pageType) {
      case gEnums.appUserProfilePageTypes.appUserAboutMe:
      case gEnums.appUserProfilePageTypes.appUserBio:
      case gEnums.appUserProfilePageTypes.appUserProfile:
        _profileData = { ..._profileData, ...formData }
        break;
      default:
        _profileData[pageType] = formData
    }
    appUser_handlers.handleStartUpdate()
    appUser_handlers.handleUpdateAppUserProfile(_profileData, profileOriginType)
  }

  const handleUpdateParentData = (d) => setFormData(d)

  const appForm = () => <AppForm
    appFormType={pageType}
    viewOnly={!aaua}
    data_parent={formData}
    handleUpdateParentData={handleUpdateParentData}
  />

  const formWrapper_save = (content) => <Wrapper
    content={content}
    wrapperType={wrapperTypes.padded}
    updating={updating}
    confirmation={confirmation}
    footer={<UiSaveButtons save={{ oc: handleSave, caption: 'Save ' + _.startCase(pageType), icon: 'save' }} />}
  />

  const content = () => {

    switch (pageType) {

      case gEnums.appUserPersonalPageTypes.connections:
        return <UserConnections />

      case gEnums.appUserPersonalPageTypes.conversations:
        return <UserConversations />

      case gEnums.appUserPersonalPageTypes.events:
        return <UserEvents />

      case gEnums.appUserPersonalPageTypes.favorites:
        return <UserFavorites />

      case gEnums.appUserPersonalPageTypes.meetings:
        return <AppMeetingsManagement forProfileItem={true} />

      case gEnums.appUserPersonalPageTypes.notes:
        return <UserNotes />

      case gEnums.appUserPersonalPageTypes.notifications:
        return <AppNotificationsManager appNotificationType={appNotificationTypes.userNotifications} />

      case gEnums.appUserPersonalPageTypes.qrCode:
        return <QrCode qrCodeType={gEnums.qrCodeTypes.appUser} qrCodeProps={qrCodeProps} />

      case gEnums.appUserPersonalPageTypes.settings:
        return <AppUserPages appFormType={appFormTypes.appUserSettings} useFullPageWrapper={false} />

      case gEnums.appUserPersonalPageTypes.ticketing:
        return <AppUserTicketingWithProvider />

      case gEnums.appUserPersonalPageTypes.videoConferencing:
        return <VideoConference forProfileItem={true} />

      case gEnums.appUserProfilePageTypes.appUserFamily:
        return <UserFamily />

      case gEnums.appUserProfilePageTypes.appUserGallery:
        return <ImageGallery
          storageLocationType={gEnums.storageLocationTypes.profileGallery}
          storageType={gEnums.storageTypes.image}
          email={email}
        />

      case gEnums.appUserProfilePageTypes.appUserProfile:
      case gEnums.appUserProfilePageTypes.appUserAboutMe:
      case gEnums.appUserProfilePageTypes.appUserBio:
        return aaua ? formWrapper_save(appForm(pageType)) : appForm(pageType)

      case gEnums.appUserProfilePageTypes.appUserProfileImage:
        return <ProfileImage email={email} />

      case gEnums.appUserPrivacyPageTypes.appUserPrivacy:
        return formWrapper_save(appForm())

      default:
        return <NoData fullCaption={'No component exists for ' + _.startCase(pageType)} />
      // nothing
    }
  }

  return <Suspense fallback={<SuspenseDimmer origin={'AppUserProfileItem'} />}>
    {content()}
  </Suspense>

}

export default AppUserProfileItem